import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Alert, Snackbar, CircularProgress, Grid, AlertColor, Box, Button, IconButton, Tooltip } from '@mui/material';
import axios from 'axios';
import { API_BASE } from '../config';

interface ImageData {
  _id: string;
  type: string;
  image: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

interface ImageDropzoneProps {
  image: ImageData;
  index: number;
  onDrop: (index: number) => (acceptedFiles: File[]) => void;
  onDelete: (id: string, index: number) => Promise<void>;
  loading: boolean;
}

const ImageDropzone: React.FC<ImageDropzoneProps> = ({ image, index, onDrop, onDelete, loading }) => {
  const isInteractionDisabled = loading;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop(index),
    disabled: isInteractionDisabled,
    accept: {
      'image/jpeg': [],
      'image/png': []
    }
  });

  return (
    <div className="group relative">
      <div
        {...getRootProps()}
        className={`dropzone flex flex-col justify-center items-center border-2 border-dashed border-gray-200 rounded-2xl text-center p-4 md:py-16 md:px-8 transition-all duration-300 ease-in-out group relative overflow-hidden ${isInteractionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-blue-500 hover:bg-blue-50/50'
          }`}
        style={{ minHeight: window.innerWidth < 768 ? '180px' : '300px' }}
      >
        <input {...getInputProps()} />

        {!image.image && (
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 md:w-16 md:h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="text-gray-400 text-xl md:text-3xl" />
            </div>
            <p className="text-xs md:text-base font-bold text-gray-400 uppercase tracking-widest leading-tight">Drop image here</p>
            <em className="text-[9px] text-gray-300 mt-1">JPEG/PNG only</em>
          </div>
        )}

        {image.image && (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-white">
            <img src={image.image} alt={`Preview ${index}`} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="bg-white/90 px-4 py-2 rounded-full shadow-lg">
                <span className="text-[10px] font-black uppercase text-blue-600 tracking-widest">
                  {isInteractionDisabled ? 'Processing...' : 'Update Image'}
                </span>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-10">
            <CircularProgress size={24} color="primary" />
          </div>
        )}
      </div>

      <div className="absolute top-2 right-2 z-20">
        <Tooltip title={isInteractionDisabled ? "Wait for completion" : "Delete Banner Slot"}>
          <span>
            <IconButton
              size="small"
              disabled={isInteractionDisabled}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(image._id, index);
              }}
              sx={{
                bgcolor: 'white',
                color: isInteractionDisabled ? '#cbd5e1' : '#ef4444',
                '&:hover': { bgcolor: isInteractionDisabled ? 'white' : '#fee2e2' },
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                border: '1px solid #fee2e2',
                cursor: isInteractionDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faTrashAlt} size="sm" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

const Banner = () => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState<boolean[]>([]);
  const [loadingMain, setLoadingMain] = useState<boolean>(true); // Start as true
  const [snackbar, setSnackbar] = useState<SnackbarState>({ severity: "success", open: false, message: '' });

  const fetchImages = async () => {
    try {
      setLoadingMain(true);
      const response = await axios.get(`${API_BASE}/api/banner/getBannerall`);

      // Ensure we have an array from the backend
      const bannersArray = Array.isArray(response.data.Banner) ? response.data.Banner : [];

      setImages(bannersArray);
      setLoading(new Array(bannersArray.length).fill(false));
    } catch (error) {
      console.error('Error fetching images:', error);
      setSnackbar({ open: true, message: 'Failed to fetch posters/banners from database.', severity: 'error' });
    } finally {
      setLoadingMain(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const onDrop = (index: number) => async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    let newImages = [...images];
    const originalImageUrl = newImages[index].image;
    newImages[index].image = previewUrl;
    setImages([...newImages]);

    setLoading(prev => {
      const newLoading = [...prev];
      newLoading[index] = true;
      return newLoading;
    });

    try {
      // Stored as a base64 data URI (not S3) so both the mobile app and the
      // webapp can render it — see fileToDataUrl below for the reasoning.
      const dataUrl = await fileToDataUrl(file);

      const response = await axios.post(`${API_BASE}/api/banner/updateBanner`, {
        type: newImages[index].type,
        image: dataUrl
      });

      if (response.data.success) {
        newImages[index] = response.data.data;
        setImages([...newImages]);
        setSnackbar({ open: true, message: 'Image updated successfully!', severity: 'success' });
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({ open: true, message: 'Failed to update image.', severity: 'error' });
      newImages[index].image = originalImageUrl;
      setImages([...newImages]);
    } finally {
      setLoading(prev => {
        const newLoading = [...prev];
        newLoading[index] = false;
        return newLoading;
      });
      URL.revokeObjectURL(previewUrl);
    }
  };

  const onDelete = async (id: string, index: number) => {
    setLoading(prev => {
      const newLoading = [...prev];
      newLoading[index] = true;
      return newLoading;
    });

    if (!id) {
      const newImages = [...images];
      newImages.splice(index, 1);
      setImages(newImages);
      setLoading(prev => {
        const newLoading = [...prev];
        newLoading.splice(index, 1);
        return newLoading;
      });
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/banner/deleteBanner/${id}`);
      const newImages = [...images];
      newImages.splice(index, 1);
      setImages(newImages);
      setLoading(prev => {
        const newLoading = [...prev];
        newLoading.splice(index, 1);
        return newLoading;
      });
      setSnackbar({ open: true, message: 'Banner deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error deleting banner:', error);
      setSnackbar({ open: true, message: 'Failed to delete banner.', severity: 'error' });
      setLoading(prev => {
        const newLoading = [...prev];
        newLoading[index] = false;
        return newLoading;
      });
    }
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const addNewBanner = () => {
    fileInputRef.current?.click();
  };

  /**
   * Resize a picked image and return it as a base64 data URI.
   *
   * Banners are stored inline in the database rather than on S3 on purpose:
   * the S3 bucket is private, and the older mobile app talks to a backend that
   * cannot sign S3 URLs — those banners would render blank there. A data URI
   * renders on every client. Images are downscaled first so the banner API
   * response (which returns every banner at once) stays small.
   */
  const fileToDataUrl = (file: File, maxWidth = 1280, quality = 0.82): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error(`Not a valid image: ${file.name}`));
        img.onload = () => {
          const scale = Math.min(1, maxWidth / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas not supported in this browser'));
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // Keep PNG for PNG sources so transparency survives; JPEG otherwise.
          const isPng = (file.type || '').includes('png');
          const dataUrl = isPng
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', quality);
          // Guard against a huge banner slowing the app's home screen for everyone.
          if (dataUrl.length > 2_000_000) {
            return reject(new Error(`${file.name} is too large even after resizing — please use a smaller image`));
          }
          resolve(dataUrl);
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      setLoadingMain(true);
      setSnackbar({ open: true, message: `Uploading ${files.length} banners...`, severity: 'info' });

      // Convert to base64 and save straight to the database (no S3 step).
      const uploadPromises = files.map(async (file, idx) => {
        const dataUrl = await fileToDataUrl(file);

        const dbRes = await axios.post(`${API_BASE}/api/banner/updateBanner`, {
          type: `BANNER_${Date.now()}_${idx}`,
          image: dataUrl
        });

        return dbRes.data.data;
      });

      await Promise.all(uploadPromises);

      // 3. Refresh the entire list from database to get official IDs/Signed URLs
      await fetchImages();
      setSnackbar({ open: true, message: 'All banners added successfully!', severity: 'success' });
    } catch (error) {
      console.error('Bulk upload error:', error);
      setSnackbar({ open: true, message: 'Failed to upload some banners.', severity: 'error' });
      fetchImages(); // Refresh to see what succeeded
    } finally {
      setLoadingMain(false);
      if (fileInputRef.current) fileInputRef.current.value = ''; // Reset input
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-10 gap-4">
          <h1 className="text-xl md:text-3xl font-black text-gray-900 uppercase tracking-tight">App Banners</h1>
          <Button
            variant="contained"
            disabled={loadingMain}
            startIcon={loadingMain ? <CircularProgress size={20} color="inherit" /> : <FontAwesomeIcon icon={faPlus} />}
            onClick={addNewBanner}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3,
              py: 1.5,
              boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)'
            }}
          >
            {loadingMain ? 'Uploading...' : 'Add New Banners'}
          </Button>
        </div>

        <Grid container spacing={3}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={6} key={image._id || index}>
              <div className="bg-white p-3 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 relative group">
                <p className="text-[10px] font-black uppercase text-gray-400 mb-3 tracking-widest pl-1">
                  {image.type || `Banner Slot ${index + 1} `}
                </p>
                <ImageDropzone
                  image={image}
                  index={index}
                  onDrop={onDrop}
                  onDelete={onDelete}
                  loading={loading[index]}
                />
              </div>
            </Grid>
          ))}
        </Grid>

        {loadingMain && (
          <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
            <CircularProgress color="primary" />
          </Box>
        )}

        {!loadingMain && images.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="text-gray-300 text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No Banners Found</h3>
            <p className="text-gray-500 max-w-xs mt-1">Start by adding your first app banner using the button above.</p>
          </div>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default Banner;
